import { useState } from "react";
import { Eye, EyeOff, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function isValidTaxNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!/^\d{10}$/.test(digits) || /^(\d)\1{9}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    let temp = (Number(digits[i]) + 9 - i) % 10;
    if (temp !== 9) temp = (temp * (2 ** (9 - i))) % 9;
    sum += temp;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === Number(digits[9]);
}

function isStrongEnough(password) {
  return password.length >= 6 && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(password) && /\d/.test(password);
}

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [accountType, setAccountType] = useState("individual");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", password: "", companyName: "", taxNumber: "", phone: "" });

  const submit = async (event) => {
    event?.preventDefault();
    try {
      setError("");
      setInfo("");
      const requiredFields = [
        { key: "fullName", label: "Ad Soyad" },
        { key: "email", label: "E-posta" },
        { key: "phone", label: "Telefon" },
        { key: "password", label: "Şifre" },
      ];
      if (accountType === "corporate") {
        requiredFields.push({ key: "companyName", label: "Firma adı" }, { key: "taxNumber", label: "Vergi numarası" });
      }
      const missingField = requiredFields.find((field) => !String(form[field.key] || "").trim());
      if (missingField) {
        setError(`${missingField.label} zorunlu.`);
        return;
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
        setError("Geçerli bir e-posta adresi gir.");
        return;
      }
      const cleanPhone = String(form.phone || "").replace(/\D/g, "");
      if (cleanPhone.length < 10) {
        setError("Geçerli bir telefon numarası gir.");
        return;
      }
      if (!isStrongEnough(form.password)) {
        setError("Şifre en az 6 karakter olmalı, harf ve rakam içermelidir.");
        return;
      }
      if (accountType === "corporate") {
        if (!form.companyName || !form.taxNumber) {
          setError("Kurumsal kayıt için firma adı ve vergi numarası zorunlu.");
          return;
        }
        if (!isValidTaxNumber(form.taxNumber)) {
          setError("Vergi numarası geçersiz. Lütfen 10 haneli geçerli VKN gir.");
          return;
        }
      }

      setBusy(true);
      const result = await register({ ...form, phone: form.phone.trim(), email: form.email.trim(), fullName: form.fullName.trim(), companyName: form.companyName.trim(), accountType });
      const warning = result.mailWarning ? ` ${result.mailWarning}` : "";
      const corporateWarning = result.corporateWarning ? ` ${result.corporateWarning}` : "";
      const corporateStatus = result.user?.corporateStatus === "pending" ? " Kurumsal hesabın manuel onay bekliyor." : "";
      setInfo(`Kayıt alındı. E-posta doğrulama kodunu girmen gerekiyor.${corporateStatus}${corporateWarning}${warning}`);
      setTimeout(() => nav(`/verify-email?email=${encodeURIComponent(form.email)}`), 1200);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="px-4 sm:px-5 md:px-10 py-10 sm:py-12 flex justify-center">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-soft p-5 sm:p-8 max-w-xl w-full">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Kayıt Ol</h1>
        {error && <div className="bg-red-50 text-red-600 p-3 rounded-xl mb-4">{error}</div>}
        {info && <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl mb-4">{info}</div>}

        <div className="grid grid-cols-2 gap-2 mb-5">
          <button type="button" onClick={() => setAccountType("individual")} className={`p-3 rounded-xl ${accountType === "individual" ? "bg-stone-900 text-white" : "bg-soft"}`}>Bireysel</button>
          <button type="button" onClick={() => setAccountType("corporate")} className={`p-3 rounded-xl ${accountType === "corporate" ? "bg-stone-900 text-white" : "bg-soft"}`}>Kurumsal</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Ad Soyad *" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="border p-3 rounded-xl" />
          <input required type="email" placeholder="E-posta *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" className="border p-3 rounded-xl" />
          <input required type="tel" inputMode="tel" placeholder="Telefon *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border p-3 rounded-xl" />
          <div className="relative">
            <input required placeholder="Şifre *" type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" className="w-full border p-3 pr-12 rounded-xl" />
            <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-950">
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <p className="flex gap-2 rounded-xl bg-stone-50 p-3 text-xs leading-5 text-stone-600 sm:col-span-2">
            <Info size={16} className="mt-0.5 shrink-0 text-emerald-700" />
            Şifre en az 6 karakter olmalı; harf, rakam ve mümkünse özel karakter içermeli. Büyük/küçük harf kullanımına dikkat et.
          </p>
          {accountType === "corporate" && (
            <>
              <div className="sm:col-span-2 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800">
                Firma adı ve VKN backend tarafında doğrulanır. Resmi API/registry unvanı ile yazdığın firma adı normalize edilip karşılaştırılır; eşleşmezse kayıt reddedilir veya manuel incelemeye alınır.
              </div>
              <input required placeholder="Firma adı *" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} className="border p-3 rounded-xl" />
              <input required inputMode="numeric" pattern="\d{10}" placeholder="Vergi numarası (10 hane) *" value={form.taxNumber} onChange={(e) => setForm({ ...form, taxNumber: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="border p-3 rounded-xl" />
            </>
          )}
        </div>

        <button type="submit" disabled={busy} className="w-full mt-5 bg-emerald-700 disabled:opacity-60 text-white py-3 rounded-xl">
          {busy ? "Kaydediliyor..." : "Kayıt Oluştur"}
        </button>
      </form>
    </section>
  );
}
